/* eslint-disable max-lines */
import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Action, createSelector, State, type StateContext, Store, select } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { type OrgaElementModel, SettingsPageTab, type SettingsStateModel } from 'commons';
import { produce } from 'immer';
import { forkJoin, map, of, switchMap } from 'rxjs';
import { LabelService } from '../../../services/api/label.service';
import { LoggingService } from '../../../services/logging.service';
import { SetStyles } from '../multi-view/actions';
import { getTreeRoots } from '../tree/tree-roots.selector';
import { echartsDefaultValue } from './echartsDefaultValue';
import * as A from './settings.actions';
import {
  AddLabel,
  DeleteLabel,
  GetLabels,
  SetAdminMode,
  SetGridConfig,
  SetGridHeight,
  SetLanguage,
  SetResolutionMultiplier,
  SetTheme,
  ToggleFilterPanel,
  ToggleSidebar,
} from './settings.actions';

type Context = StateContext<SettingsStateModel>;

@State<SettingsStateModel>({
  name: 'settings_state',
  defaults: {
    filterPanelVisibility: false,
    debug: false,
    isAdmin: false,
    search: '',
    enabledAutoFresh: [],
    pollingInterval: 30000,
    databrowserIsCollapsed: false,
    currentSettingPageTab: SettingsPageTab.APPLICATION,
    bu_settings: {},
    sidebarExpanded: false,
    language: 'os',
    theme: 'light',
    resolutionMultiplier: 1,
    echartsConfig: echartsDefaultValue,
  },
})
@Injectable()
export class SettingsState {
  private readonly logging = inject(LoggingService);
  private readonly labelService = inject(LabelService);
  private readonly store = inject(Store);
  private readonly transloco = inject(TranslocoService);
  /**
   *
   */
  roots = select(getTreeRoots);
  //  █████╗  ██████╗████████╗██╗ ██████╗ ███╗   ██╗
  // ██╔══██╗██╔════╝╚══██╔══╝██║██╔═══██╗████╗  ██║
  // ███████║██║        ██║   ██║██║   ██║██╔██╗ ██║
  // ██╔══██║██║        ██║   ██║██║   ██║██║╚██╗██║
  // ██║  ██║╚██████╗   ██║   ██║╚██████╔╝██║ ╚████║
  // ╚═╝  ╚═════╝   ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝
  /**
   * Saves the current tab position in the settings state.
   * @param ctx - The state context.
   * @param position - The new tab position to save.
   */
  @Action(A.SaveTabPosition) saveTabPosition(ctx: Context, { position }: A.SaveTabPosition) {
    ctx.patchState({ currentSettingPageTab: position });
  }
  /**
   * Toggles the debug mode in the settings state.
   * @param ctx - The state context.
   */
  @Action(A.ToggleDebugMode) toggle_debug_mode(ctx: Context) {
    ctx.patchState({ debug: !ctx.getState().debug });
  }
  /**
   * Toggles the collapse state of the data browser and updates its styles.
   * @param ctx - The state context.
   */
  @Action(A.DataBrowserCollapseAction) handleDataBrowserCollapse(ctx: Context) {
    ctx.patchState({
      databrowserIsCollapsed: !ctx.getState().databrowserIsCollapsed,
    });
    if (ctx.getState().databrowserIsCollapsed) {
      ctx.dispatch(new SetStyles('datatree', { width: '0%' }));
    } else {
      ctx.dispatch(new SetStyles('datatree', { width: '15%' }));
    }
  }
  /**
   * Updates the search state and logs the search event.
   * @param ctx - The state context.
   * @param action - The search action containing the search value.
   */
  @Action(A.Search) search(ctx: StateContext<{ search: string }>, action: A.Search) {
    const searchState = { search: action.payload.value };
    ctx.patchState(searchState);
    this.logging.logEvent('search channel', searchState);
  }
  /**
   * Toggles the auto-refresh state for a specific datasource.
   * @param ctx - The state context.
   * @param action - The action containing the datasource to toggle.
   */
  @Action(A.ToggleDatasourceRefresh) handleToggleDatasourceRefresh(ctx: Context, action: A.ToggleDatasourceRefresh) {
    const enabled = ctx.getState().enabledAutoFresh;
    const isEnabled = enabled.includes(action.datasource);
    ctx.patchState({
      enabledAutoFresh: isEnabled ? [...enabled.filter((datasource) => datasource !== action.datasource)] : [...enabled, action.datasource],
    });
  }
  /**
   * Sets the admin mode in the settings state.
   * @param ctx - The state context.
   * @param isAdmin - Whether admin mode should be enabled.
   */
  @Action(SetAdminMode) setAdminMode(ctx: Context, { isAdmin }: SetAdminMode) {
    ctx.setState(patch({ isAdmin }));
  }
  /**
   * Toggles the visibility of the filter panel.
   * @param ctx - The state context.
   */
  @Action(ToggleFilterPanel) toggleFilterPanel(ctx: Context) {
    ctx.patchState({
      filterPanelVisibility: !ctx.getState().filterPanelVisibility,
    });
  }
  /**
   * Fetches labels for the root business units and updates the state.
   * @param ctx - The state context.
   */
  @Action(GetLabels) getLabels(ctx: StateContext<SettingsStateModel>) {
    const roots = this.store.selectSnapshot(getTreeRoots).slice(0, 1);
    if (!roots) {
      return;
    }
    const calls = roots.map((root) => {
      const bu_id = (root?.entity as OrgaElementModel)?.businessUnitIdentifier;
      return this.labelService.getLabelByBusinessUnitId(bu_id).pipe(
        switchMap((labels) => {
          return of({ [bu_id]: labels });
        })
      );
    });
    forkJoin(calls)
      .pipe(
        map((labels) => {
          return labels.reduce((acc, curr) => {
            // biome-ignore lint/performance/noAccumulatingSpread: can't be avoided due to the way the data is structured
            return { ...acc, ...curr };
          }, {});
        })
      )
      .subscribe((labelMap) => {
        ctx.setState(
          produce(ctx.getState(), (draft) => {
            Object.keys(labelMap).forEach((bu_id: string) => {
              draft.bu_settings[bu_id] = {
                labels: { available: labelMap[bu_id] },
              };
            });
          })
        );
      });
  }
  /**
   * Fetches labels for a specific business unit and updates the state.
   * @param ctx - The state context.
   * @param bu_id - The business unit identifier.
   */
  @Action(A.GetLabelsOfBusinessUnit) getLabelsOfBusinessUnit(ctx: Context, { bu_id }: A.GetLabelsOfBusinessUnit) {
    this.labelService.getLabelByBusinessUnitId(bu_id).subscribe((labels) => {
      ctx.setState(
        produce(ctx.getState(), (draft) => {
          draft.bu_settings[bu_id] = { labels: { available: labels } };
        })
      );
    });
  }
  /**
   * Adds a new label to a specific business unit.
   * @param ctx - The state context.
   * @param label - The label to add.
   * @param bu_id - The business unit identifier.
   */
  @Action(AddLabel) addLabel(ctx: Context, { label, bu_id }: AddLabel) {
    if (!label.label) {
      return;
    }
    this.labelService
      .createLabel({
        businessUnitId: bu_id,
        color: '#1C98B5',
        name: label.label,
      })
      .subscribe((labelEntity) => {
        ctx.setState(
          produce(ctx.getState(), (draft) => {
            if (!draft.bu_settings[bu_id]?.labels?.available) {
              draft.bu_settings[bu_id] = { labels: { available: [] } };
            }
            if (!draft.bu_settings[bu_id].labels.available) {
              return;
            }
            draft.bu_settings[bu_id].labels.available = [...draft.bu_settings[bu_id].labels.available, labelEntity];
          })
        );
      });
  }
  /**
   * Toggles the sidebar expansion state.
   * @param ctx - The state context.
   */
  @Action(ToggleSidebar) toggleSidebar(ctx: Context) {
    ctx.patchState({
      sidebarExpanded: !ctx.getState().sidebarExpanded,
    });
  }
  /**
   * Deletes a label from a specific business unit.
   * @param ctx - The state context.
   * @param labelId - The identifier of the label to delete.
   * @param bu_id - The business unit identifier.
   */
  @Action(DeleteLabel) deleteLabel(ctx: Context, { labelId, bu_id }: DeleteLabel) {
    this.labelService.deleteLabel(labelId).subscribe();
    ctx.setState(
      produce(ctx.getState(), (draft) => {
        if (!draft.bu_settings[bu_id]?.labels?.available) {
          return;
        }
        draft.bu_settings[bu_id].labels.available = draft.bu_settings[bu_id].labels.available.filter((label) => label.identifier !== labelId);
      })
    );
  }

  /**
   * Sets the language in the settings state.
   * @param ctx - The state context.
   * @param language - The language to set.
   */
  @Action(SetLanguage)
  setLanguage(ctx: Context, { language }: SetLanguage) {
    ctx.patchState({ language });
  }

  /**
   * Sets the theme in the settings state.
   * @param ctx - The state context.
   * @param theme - The theme to set.
   */
  @Action(SetTheme)
  setTheme(ctx: Context, { theme }: SetTheme) {
    ctx.patchState({ theme });
  }

  /**
   * Sets the resolution multiplier in the settings state.
   * @param ctx - The state context.
   * @param resolutionMultiplier - The resolution multiplier to set.
   */
  @Action(SetResolutionMultiplier)
  setResolutionMultiplier(ctx: Context, { resolutionMultiplier }: SetResolutionMultiplier) {
    ctx.patchState({ resolutionMultiplier });
  }

  /**
   * Sets the grid height in the settings state.
   * @param ctx - The state context.
   * @param gridHeight - The grid height to set.
   */
  @Action(SetGridHeight)
  setGridHeight(ctx: Context, { gridHeight }: SetGridHeight) {
    ctx.patchState({
      echartsConfig: { ...ctx.getState().echartsConfig, gridHeight },
    });
  }

  /**
   * Sets multiple grid configuration values in the settings state.
   * @param ctx - The state context.
   * @param config - The grid configuration values to set.
   */
  @Action(SetGridConfig)
  setGridConfig(ctx: Context, { config }: SetGridConfig) {
    ctx.patchState({
      echartsConfig: { ...ctx.getState().echartsConfig, ...config },
    });
  }

  @Action(A.ApplyLanguage)
  applyLanguage(ctx: Context, { language }: A.ApplyLanguage) {
    const currentLanguage = ctx.getState().language;
    const defaultLanguageCode = navigator.language.split('-')[0];

    if (language && language !== currentLanguage) {
      ctx.dispatch(new SetLanguage(language));
    } else if (!language) {
      // Use persisted language from state, or browser language if set to 'os'
      if (currentLanguage === 'os') {
        this.transloco.setActiveLang(defaultLanguageCode);
      } else {
        this.transloco.setActiveLang(currentLanguage);
      }
    }
  }

  /**
   * Retrieves the current language from the settings state.
   */
  static readonly getLanguage = createSelector([SettingsState], (state: SettingsStateModel) => state.language);

  /**
   * Retrieves the current theme from the settings state.
   */
  static readonly getTheme = createSelector([SettingsState], (state: SettingsStateModel) => state.theme);

  /**
   * Checks if the admin mode is enabled in the settings state.
   */
  static readonly isAdmin = createSelector([SettingsState], (state: SettingsStateModel) => state.isAdmin);

  /**
   * Retrieves the current settings page tab from the settings state.
   */
  static readonly currentPageTab = createSelector([SettingsState], (state: SettingsStateModel) => state.currentSettingPageTab);

  /**
   * Retrieves all available labels from the settings state.
   */
  static readonly getLabels = createSelector([SettingsState], (state: SettingsStateModel) =>
    Object.values(state?.bu_settings).flatMap((bu) => bu?.labels?.available || [])
  );

  /**
   * Checks if the sidebar is expanded in the settings state.
   */
  static readonly isSidebarExpanded = createSelector([SettingsState], (state: SettingsStateModel) => state.sidebarExpanded);

  /**
   * Gets the current resolution multiplier from the settings state.
   */
  static readonly resolutionMultiplier = createSelector([SettingsState], (state: SettingsStateModel) => state.resolutionMultiplier);

  /**
   * Gets the current grid height from the settings state.
   */
  static readonly gridHeight = createSelector([SettingsState], (state: SettingsStateModel) => state?.echartsConfig?.gridHeight);

  /**
   * Gets all grid configuration values from the settings state.
   */
  static readonly gridConfig = createSelector([SettingsState], (state: SettingsStateModel) => ({
    ...state?.echartsConfig,
  }));
}
